import React, { useState, useContext, useRef, useEffect } from "react";
import Swal from "sweetalert2";

import AuthService from "../../../../services/Admin/AuthService";
import CompanyService from "../../../../services/Admin/CompanyService";
import MessagingService from "../../../../services/Admin/MessagingService";
import HttpError from "../../../../utill/HttpError";
import LoadingContext from "../../../../store/loading-context";

/* ------------------------------------------------------------------ */
/*  constants                                                          */
/* ------------------------------------------------------------------ */
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024; // 2 MB per slide
const ALLOWED_MIME = ["image/jpeg", "image/jpg", "image/png"];
const ALLOWED_EXT = ["jpg", "jpeg", "png"];

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/* ------------------------------------------------------------------ */
/*  helpers                                                            */
/* ------------------------------------------------------------------ */
const makeUid = () =>
  `slide_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const toSlide = (item = {}) => ({
  uid: makeUid(),
  image_link: item.image_link || "",
  target_link: item.target_link || "",
});

/** move item from `from` to `to` without mutating the original array */
const reorder = (list, from, to) => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/* ------------------------------------------------------------------ */
/*  upload via backend proxy + precheck                                */
/* ------------------------------------------------------------------ */
const uploadImage = async (file) => {
  const token = AuthService.getAccessToken();

  /* calls MessagingService.imgToLink(token, file) — backend endpoint */
  const json = await MessagingService.imgToLink(token, file);

  if (!json || json.status !== "SUCCESS" || !json.data || !json.data.url) {
    const message =
      (json && json.message) || "Upload failed. Please try again.";
    throw new Error(message);
  }

  return json.data.url;
};

const precheckFile = (file) =>
  new Promise((resolve) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext) || !ALLOWED_MIME.includes(file.type)) {
      return resolve({
        ok: false,
        reason: "Only JPG, JPEG, or PNG images are allowed.",
      });
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      return resolve({
        ok: false,
        reason: `Image is ${formatBytes(file.size)}. Max upload is 2 MB.`,
      });
    }

    resolve({ ok: true, size: file.size });
  });

/* ------------------------------------------------------------------ */
/*  component                                                          */
/* ------------------------------------------------------------------ */
const SliderImages = ({ data, onSaved }) => {
  const loadingCtx = useContext(LoadingContext);

  const [slides, setSlides] = useState(() =>
    ((data && data.slider_images) || []).map(toSlide),
  );

  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [saving, setSaving] = useState(false);

  const rowRefs = useRef([]);

  /* per-slide transient upload state, keyed by uid */
  const [uploadState, setUploadState] = useState({});
  const fileInputsRef = useRef({});

  /* ---------- cleanup object URLs on unmount ---------- */
  useEffect(() => {
    return () => {
      Object.values(uploadState).forEach((u) => {
        if (u && u.preview) URL.revokeObjectURL(u.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------- crud ------------------------- */
  const addSlide = () => setSlides((prev) => [...prev, toSlide()]);

  const removeSlide = (slideUid) => {
    const state = uploadState[slideUid];
    if (state && state.preview) URL.revokeObjectURL(state.preview);
    setUploadState((prev) => {
      const next = { ...prev };
      delete next[slideUid];
      return next;
    });
    setSlides((prev) => prev.filter((s) => s.uid !== slideUid));
  };

  const changeSlide = (slideUid, field, value) =>
    setSlides((prev) =>
      prev.map((s) => (s.uid === slideUid ? { ...s, [field]: value } : s)),
    );

  const moveSlide = (index, delta) =>
    setSlides((prev) => {
      const to = index + delta;
      if (to < 0 || to >= prev.length) return prev;
      return reorder(prev, index, to);
    });

  /* --------------------- drag and drop --------------------- */
  const clearDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragStart = (event, index) => {
    setDragIndex(index);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));

    const row = rowRefs.current[index];
    if (row) event.dataTransfer.setDragImage(row, 24, 24);
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    if (dragIndex === null || dragIndex === index) return;
    if (overIndex !== index) setOverIndex(index);
  };

  const handleDrop = (event, index) => {
    event.preventDefault();

    const from =
      dragIndex !== null
        ? dragIndex
        : Number(event.dataTransfer.getData("text/plain"));

    if (Number.isInteger(from) && from !== index) {
      setSlides((prev) => reorder(prev, from, index));
    }

    clearDrag();
  };

  /* --------------------- upload per slide --------------------- */
  const setUpload = (uid, patch) =>
    setUploadState((prev) => ({
      ...prev,
      [uid]: { ...(prev[uid] || {}), ...patch },
    }));

  const handleFile = async (slide, file) => {
    if (!file) return;

    const uid = slide.uid;

    /* reset file input value so re-picking the same file fires change */
    if (fileInputsRef.current[uid]) {
      fileInputsRef.current[uid].value = "";
    }

    /* revoke old preview */
    const previous = uploadState[uid];
    if (previous && previous.preview) URL.revokeObjectURL(previous.preview);

    setUpload(uid, { error: "", fileName: file.name, uploading: true });

    const pre = await precheckFile(file);

    if (!pre.ok) {
      setUpload(uid, {
        error: pre.reason,
        fileName: "",
        uploading: false,
        preview: "",
      });
      Swal.fire("Invalid image", pre.reason, "error");
      return;
    }

    const preview = URL.createObjectURL(file);
    setUpload(uid, { preview });

    try {
      const url = await uploadImage(file);
      /* write returned URL into the slide itself */
      changeSlide(uid, "image_link", url);
      setUpload(uid, { uploading: false, error: "" });
    } catch (err) {
      setUpload(uid, {
        uploading: false,
        error: err.message || "Upload failed.",
      });
      /* leave preview so user can see what failed */
    }
  };

  const onFilePick = (slide, e) => {
    const file = e.target.files && e.target.files[0];
    handleFile(slide, file);
  };

  const onDrop = (slide, e) => {
    e.preventDefault();
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    handleFile(slide, file);
  };

  const clearSlideImage = (slide) => {
    const uid = slide.uid;
    const state = uploadState[uid];
    if (state && state.preview) URL.revokeObjectURL(state.preview);
    setUpload(uid, {
      preview: "",
      fileName: "",
      error: "",
      uploading: false,
    });
    changeSlide(uid, "image_link", "");
  };

  /* ------------------------- save ------------------------- */
  const saveHandler = async () => {
    /* block save if any slide is still uploading */
    const anyUploading = Object.values(uploadState).some(
      (u) => u && u.uploading,
    );
    if (anyUploading) {
      Swal.fire(
        "Opps!",
        "Please wait for all image uploads to finish.",
        "info",
      );
      return;
    }

    const payload = slides
      .map(({ image_link, target_link }) => ({
        image_link: image_link.trim(),
        target_link: target_link.trim(),
      }))
      .filter((s) => s.image_link !== "" || s.target_link !== "");

    const emptyIndex = payload.findIndex((s) => !s.image_link);
    if (emptyIndex !== -1) {
      Swal.fire(
        "Opps!",
        `Slide #${emptyIndex + 1} is missing an image link.`,
        "warning",
      );
      return;
    }

    loadingCtx.startLoading();
    setSaving(true);

    try {
      const token = AuthService.getAccessToken();
      const responseData = await CompanyService.updateSliderImages(
        token,
        payload,
      );

      const { status, message } = responseData;

      if (status === "SUCCESS") {
        Swal.fire("Success!", message, "success");
        if (onSaved) onSaved(responseData);
      } else {
        Swal.fire("Opps!", message, "error");
      }
    } catch (error) {
      Swal.fire("Opps!", HttpError.getError(error), "error");
    } finally {
      loadingCtx.stopLoading();
      setSaving(false);
    }
  };

  /* ------------------------ render ------------------------ */
  return (
    <React.Fragment>
      <style>{styles}</style>

      <div className="row">
        <div className="col-md-10 mx-auto">
          <div className="card bg-light d-flex flex-fill">
            <div className="card-header border-bottom-0 d-flex align-items-center justify-content-between">
              <h3 className="card-title mb-0">
                Slider Images
                <span className="badge badge-info ml-2">{slides.length}</span>
              </h3>

              <button
                type="button"
                className="btn btn-info btn-sm"
                onClick={addSlide}
              >
                <i className="fas fa-plus mr-1" /> Add
              </button>
            </div>

            <div className="card-body pt-0 mb-2">
              {slides.length === 0 && (
                <div className="text-center text-muted py-4">
                  <i className="far fa-images fa-2x d-block mb-2" />
                  No slider images yet — click <strong>Add</strong> to create
                  one.
                </div>
              )}

              {slides.map((slide, index) => {
                const dragging = dragIndex === index;
                const dropTarget = overIndex === index && dragIndex !== index;
                const state = uploadState[slide.uid] || {};
                const uploading = !!state.uploading;
                /* prefer local preview while uploading, otherwise the actual image_link */
                const previewSrc = state.preview || slide.image_link;

                return (
                  <div
                    key={slide.uid}
                    ref={(el) => {
                      rowRefs.current[index] = el;
                    }}
                    className={
                      "slider-row" +
                      (dragging ? " slider-row--dragging" : "") +
                      (dropTarget ? " slider-row--over" : "") +
                      (uploading ? " slider-row--busy" : "")
                    }
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    {/* drag handle */}
                    <div
                      className="slider-row__handle"
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={clearDrag}
                      title="Drag to change order"
                    >
                      <i className="fas fa-grip-vertical" />
                    </div>

                    {/* serial number */}
                    <div className="slider-row__index">{index + 1}</div>

                    {/* live preview */}
                    <div className="slider-row__thumb">
                      <i className="far fa-image" />
                      {previewSrc && (
                        <img
                          src={previewSrc}
                          alt=""
                          onLoad={(e) => {
                            e.currentTarget.style.display = "block";
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}
                      {uploading && (
                        <div className="slider-row__thumb-overlay">
                          <i className="fas fa-spinner fa-spin" />
                        </div>
                      )}
                    </div>

                    {/* fields */}
                    <div className="slider-row__fields">
                      <div className="form-group">
                        <label htmlFor={`image-${slide.uid}`}>Image link</label>
                        <div className="slider-row__input-row">
                          <input
                            id={`image-${slide.uid}`}
                            type="text"
                            className={
                              "form-control form-control-sm" +
                              (state.error ? " is-invalid" : "")
                            }
                            placeholder="https://example.com/banner.jpg"
                            value={slide.image_link}
                            onChange={(e) =>
                              changeSlide(
                                slide.uid,
                                "image_link",
                                e.target.value,
                              )
                            }
                          />

                          {/* hidden file input */}
                          <input
                            ref={(el) => {
                              fileInputsRef.current[slide.uid] = el;
                            }}
                            type="file"
                            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                            onChange={(e) => onFilePick(slide, e)}
                            hidden
                          />

                          <button
                            type="button"
                            className="slider-row__upload-btn"
                            onClick={() =>
                              !uploading &&
                              fileInputsRef.current[slide.uid]?.click()
                            }
                            disabled={uploading}
                            title="Upload image"
                          >
                            {uploading ? (
                              <i className="fas fa-spinner fa-spin" />
                            ) : (
                              <i className="fas fa-cloud-upload-alt" />
                            )}
                          </button>
                        </div>

                        {state.error && (
                          <small className="slider-row__err">
                            {state.error}
                          </small>
                        )}
                        {!state.error && state.fileName && !uploading && (
                          <small className="slider-row__ok">
                            <i className="fas fa-check-circle mr-1" />
                            Uploaded · {state.fileName}
                          </small>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor={`target-${slide.uid}`}>
                          Target link
                        </label>
                        <input
                          id={`target-${slide.uid}`}
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="https://example.com/page"
                          value={slide.target_link}
                          onChange={(e) =>
                            changeSlide(
                              slide.uid,
                              "target_link",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </div>

                    {/* actions */}
                    <div className="slider-row__actions">
                      {previewSrc && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => clearSlideImage(slide)}
                          title="Clear image"
                          disabled={uploading}
                        >
                          <i className="fas fa-eraser" />
                        </button>
                      )}

                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={index === 0}
                        onClick={() => moveSlide(index, -1)}
                        title="Move up"
                      >
                        <i className="fas fa-chevron-up" />
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={index === slides.length - 1}
                        onClick={() => moveSlide(index, 1)}
                        title="Move down"
                      >
                        <i className="fas fa-chevron-down" />
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeSlide(slide.uid)}
                        title="Remove"
                      >
                        <i className="far fa-trash-alt" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center mb-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveHandler}
              disabled={saving}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-1" /> Saving…
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-1" /> Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

/* ------------------------------------------------------------------ */
/*  styles                                                             */
/* ------------------------------------------------------------------ */
const styles = `
.slider-row{display:flex;align-items:center;gap:12px;padding:10px 12px;margin-bottom:10px;background:#fff;border:1px solid #e3e6ea;border-radius:.5rem;transition:border-color .15s ease,box-shadow .15s ease,opacity .15s ease;}
.slider-row:hover{box-shadow:0 2px 8px rgba(0,0,0,.06);}
.slider-row--dragging{opacity:.4;}
.slider-row--over{border-color:#17a2b8;box-shadow:0 0 0 3px rgba(23,162,184,.18);}
.slider-row--busy{opacity:.85;}
.slider-row__handle{cursor:grab;color:#b6bcc4;padding:4px 2px;user-select:none;-webkit-user-select:none;touch-action:none;}
.slider-row__handle:hover{color:#17a2b8;}
.slider-row__handle:active{cursor:grabbing;}
.slider-row__index{flex:0 0 auto;width:26px;height:26px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:#eef4f7;color:#17a2b8;font-size:.75rem;font-weight:700;}

.slider-row__thumb{position:relative;flex:0 0 auto;width:64px;height:44px;display:flex;align-items:center;justify-content:center;background:#f4f6f9;border:1px dashed #d6dbe1;border-radius:.35rem;color:#b6bcc4;overflow:hidden;}
.slider-row__thumb img{position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;}
.slider-row__thumb-overlay{position:absolute;inset:0;background:rgba(15,23,42,.55);color:#fff;display:flex;align-items:center;justify-content:center;font-size:.9rem;}

.slider-row__fields{display:flex;flex:1 1 auto;gap:12px;min-width:0;}
.slider-row__fields .form-group{flex:1 1 0;min-width:0;margin-bottom:0;}
.slider-row__fields label{font-size:.7rem;text-transform:uppercase;letter-spacing:.04em;color:#8b95a1;margin-bottom:2px;}

.slider-row__input-row{position:relative;display:flex;gap:6px;align-items:center;}
.slider-row__input-row .form-control{flex:1 1 auto;min-width:0;}
.slider-row__upload-btn{flex:0 0 auto;width:32px;height:32px;border:1px solid #d8dee7;background:#f7f9fc;color:#5b6572;border-radius:.35rem;cursor:pointer;font-size:.8rem;display:inline-flex;align-items:center;justify-content:center;transition:all .12s ease;}
.slider-row__upload-btn:hover:not(:disabled){border-color:#17a2b8;color:#17a2b8;background:#eef6f9;}
.slider-row__upload-btn:disabled{opacity:.55;cursor:wait;}

.slider-row__err{display:block;font-size:.72rem;color:#e3342f;margin-top:3px;}
.slider-row__ok{display:block;font-size:.72rem;color:#1a9c4a;margin-top:3px;}

.slider-row__actions{display:flex;gap:6px;flex:0 0 auto;}

@media (max-width:767.98px){
  .slider-row{flex-wrap:wrap;}
  .slider-row__fields{flex-direction:column;flex-basis:100%;}
  .slider-row__actions{margin-left:auto;}
}
`;

export default SliderImages;
